import { RouterPaths } from 'src/components/Router/Router.constants';
import { eAnalyticsTab } from 'src/pages/Analytics/Analytics.enums';
import { SettingsTabs } from 'src/pages/Settings/Settings.enum';
import { eUserRole } from 'src/providers/UserProvider/User.enums';
import {
  tAlertPriority,
  tAlertSnapshot,
  tAlertSurface,
} from './AlertCenter.types';

/** Shared i18n namespace for every alert (KTD12). */
export const ALERT_CENTER_NS = 'common/alertCenter';

/**
 * Ordering weight. Lower sorts first. Selection never compares the string
 * values directly — thresholds and orderings live here so tests can import
 * them rather than hardcoding, per the repo's business-rule learning.
 */
export const ALERT_PRIORITY_ORDER: Record<tAlertPriority, number> = {
  p0: 0,
  p1: 1,
  p2: 2,
  p3: 3,
};

export const ALERT_PRIORITIES: tAlertPriority[] = ['p0', 'p1', 'p2', 'p3'];

export const ALL_ROLES: eUserRole[] = [
  eUserRole.OWNER,
  eUserRole.ADMIN,
  eUserRole.SELLER,
  eUserRole.VIEWER,
];

export const OWNER_ONLY: eUserRole[] = [eUserRole.OWNER];
export const OWNER_AND_ADMIN: eUserRole[] = [eUserRole.OWNER, eUserRole.ADMIN];
export const SELLER_ONLY: eUserRole[] = [eUserRole.SELLER];

/** Surfaces the Lead Agent paywall alerts reach, mirroring `resolveSurface`. */
export const LEAD_AGENT_SURFACES: tAlertSurface[] = [
  'home',
  'funnels',
  'automations',
];

/** R6/R11 — the quota warning tier starts here. Imported by tests. */
export const QUOTA_WARNING_THRESHOLD = 0.8;

/** KTD16 — capacity warning tier for the Lead Agent usage alert. */
export const LEAD_AGENT_CAPACITY_WARNING_THRESHOLD = 80;

/**
 * D4 — the window arbitration table (validated 2026-08-19, owner: product).
 * One wide critical at a time: the P0 whose resolution window is SHORTEST
 * wins the strip, because it is the one where acting now changes the outcome.
 * Lower rank = shorter window = wins.
 *
 * The order is channel-first: with the channel down nothing else enters the
 * funnel, so `wapi-disconnected` outranks everything — a full quota is
 * irrelevant while no lead can arrive. `ww-banned` beats `quota-reached` for
 * the same upstream reason, and sits above `account-health` because a ban has
 * a guided, fast recovery path while health restrictions resolve on Meta's
 * clock. `la-usage-capped` closes the table: it self-resolves at the billing
 * cut-off even if nobody acts.
 *
 * Every P0 in `ALERT_REGISTRY` must appear here exactly once, with a unique
 * rank — the static exhaustiveness test fails on any orphan or gap.
 */
export const ALERT_WINDOW_RANK: Record<string, number> = {
  'wapi-disconnected': 0,
  'ww-banned': 1,
  'quota-reached': 2,
  'account-health': 3,
  'la-usage-capped': 4,
  /*
   * U12/U13 (RET-1269) — the ladder's two loud rungs.
   *
   * 🔴 PROVISIONAL RANKS, owner: Ariel (Alert Center). They close the table
   * because the arbitration order is CHANNEL FIRST: with the channel down or
   * the quota full nothing enters the funnel at all, while an unpaid account
   * keeps working right up to cancellation (KTD5 — nothing is blocked before
   * the bound). Placing them last is the conservative reading; whether a
   * cancellation days away should outrank a Meta health restriction is a
   * product call this unit does not get to make alone.
   *
   * TWO entries for what a single viewer experiences as one rung: ranks are
   * GLOBAL, not per role, and the static exhaustiveness test fails on any P0
   * missing from this table. The two can never contend — disjoint roles, and
   * `applyExclusivity` runs after the role filter — so the relative order
   * between them is arbitrary and only their uniqueness matters.
   */
  'past-due-final': 5,
  'past-due-seller-final': 6,
  'plan-pause-active': 7,
  /*
   * INT-1522 (Fase 3/3 de INT-715) — 🔴 PROVISIONAL RANK, owner: product.
   * Placed last, conservatively: unlike wapi-disconnected (WhatsApp API,
   * this business's primary channel), a dead Messenger/Instagram token
   * silently fails ONE channel while WhatsApp and the rest of the funnel
   * keep working, so it should not automatically outrank every other P0.
   * Whether it deserves a higher tie-break rank is a product call this unit
   * does not get to make alone.
   */
  'meta-messenger-token-invalid': 8,
};

export const PLAN_PAUSE_SUBSCRIPTION_TARGET = `${RouterPaths.settings}?section=${SettingsTabs.SUBSCRIPTION}`;

/**
 * Alerts whose `in-page` surface is a component that PREDATES the Alert Center
 * and is deliberately left outside the RET-1879 retirement gate:
 * `user-inactive` is rendered by `InactiveUserBanner` (R15) and
 * `subscription-promo` by `SubscriptionBanner` (R16). They declare `in-page` so
 * the engine knows where they live; `AlertInPage` refuses to paint them, since
 * putting the same fact on the same screen twice is the regression RET-1879
 * fixed.
 *
 * 🔴 THE CONSEQUENCE IS NOT ONLY VISUAL, which is why this list is registry
 * knowledge rather than one component's private detail: because no component
 * paints them here, nothing calls `registerInlineAlert` for them either — the
 * four registration sites are `AlertInPage`, `AlertBanner`, `SidebarAvatar` and
 * `TopDescription`, and the two legacy banners are none of them. Anything that
 * reasons about "is this fact already permanently on screen?" has to consult
 * this list directly; see `permanentBellAlerts`.
 */
export const LEGACY_HOSTED_IN_PAGE_ALERT_IDS: string[] = [
  'user-inactive',
  'subscription-promo',
];

// ─── The past-due ladder (U12/U13 · RET-1269) ────────────────────────────────

/**
 * The four declarations, in registry order. Exported so tests and surfaces
 * name the family once instead of re-listing ids that could drift apart.
 */
export const PAST_DUE_ALERT_IDS: string[] = [
  'past-due-early',
  'past-due-final',
  'past-due-seller',
  'past-due-seller-final',
];

/**
 * The ladder's two day boundaries, as RANGES and never equalities.
 *
 * The modal chain this replaces fired on `days === 3`, `days === 5`,
 * `days === 10`: a customer whose first session of the week landed on day 2
 * met nothing at all. Days 0-4 hold 76% of recoveries, so that is precisely
 * the interval an exact match cannot afford to miss.
 *
 * Defined here rather than inline so the placement resolver, the prominence
 * resolver and the copy resolver cannot drift apart by a day — the same reason
 * `leadAgentExpiredPhase` is one function.
 */
export const PAST_DUE_WARN_DAY = 5;
export const PAST_DUE_FINAL_DAY = 9;

/** The owner's last past-due rung, the one the wide strip carries (RET-2452). */
export const PAST_DUE_FINAL_ALERT_ID = 'past-due-final';

/**
 * Where the pay CTA lands when the BFF publishes no hosted invoice URL.
 *
 * A past-due workspace whose `openInvoiceSource` is `none` has no Stripe page
 * to send anyone to, and an `external` CTA with no target is stripped before
 * rendering (`withActionableCta`) — which would leave the owner of a
 * delinquent account looking at a notice with no way to act on it, on exactly
 * the rungs where paying matters most. Ajustes -> Suscripcion is where the
 * payment method lives, so it is the honest fallback rather than no button.
 */
export const PAST_DUE_SUBSCRIPTION_TARGET = `${RouterPaths.settings}?section=${SettingsTabs.SUBSCRIPTION}&cta_type=past_due_alert`;

/**
 * Stripe decline codes that get the "your bank refused it without saying why"
 * line (~14% of failures).
 *
 * The set is CLOSED on purpose. `insufficient_funds` (69% of failures) and
 * `expired_card` (2%) have their own advice, and anything outside all three
 * buckets renders NO reason line at all — the copy degrades to the plain
 * wording rather than guessing at a code Stripe introduced last week.
 */
export const PAST_DUE_GENERIC_DECLINE_CODES: readonly string[] = [
  'do_not_honor',
  'generic_decline',
  'card_declined',
  'transaction_not_allowed',
  'processing_error',
  'try_again_later',
  'issuer_not_available',
  'service_not_allowed',
];

// ─── CTA targets (U4 / RET-1878 — F4: a declared CTA must lead somewhere) ────

/**
 * M6/M8 — the quota CTA for owner/admin, byte-identical to what the legacy
 * banners navigate (`ActiveLeadsQuotaBanner.handleUpgradeClick`,
 * `Pipe.hook.handleUpgradeOrIncreaseLimit`): Business/Advanced land on the
 * lead-limit add-on modal, Growth on the plans view.
 */
export function resolveQuotaSubscriptionTarget(
  snapshot: tAlertSnapshot,
): string {
  const upgradeParam = snapshot.isLeadLimitAddonEligiblePlan
    ? 'openLeadLimitAddon=true'
    : 'upgrade=true';
  return `${RouterPaths.settings}?section=${SettingsTabs.SUBSCRIPTION}&${upgradeParam}`;
}

/**
 * R16 — the promo alert lands on the same subscription section the in-page
 * `SubscriptionBanner` navigates to; `cta_type` mirrors that banner's
 * analytics param so Settings attributes the visit the same way.
 */
export const SUBSCRIPTION_PROMO_TARGET = `${RouterPaths.settings}?section=${SettingsTabs.SUBSCRIPTION}&cta_type=banner`;

/**
 * The Smart Leadbot config page. Where the Lead Agent is TURNED ON and
 * configured — and nothing else.
 *
 * The original claim here was that "every LA state (capacity, trial, expiry,
 * acceptance) is visible and actionable" on that page, so every LA alert
 * routed to it (plan U4). That claim is false and has been walked back three
 * times: RET-1956 for `la-expired` and `la-acceptance` ("the config page
 * cannot pay"), the compact pass for `la-trial-cap`, and this pass for the
 * capacity and shutdown families. A `grep` for tier/purchase modals over
 * `src/pages/SmartLeadbotConfig/` returns nothing — the Lead Agent's tier is
 * chosen in `Analytics → Lead Agent Stats → PlanCard`, and its reactivation
 * card lives in Ajustes → Suscripción.
 *
 * So: only `la-trial-activation` still points here, because activating IS a
 * configuration action. Anything that asks the Owner to PAY carries the
 * checkout in its own detail renderer instead (the RET-1956 pattern).
 */
export const LEAD_AGENT_CONFIG_TARGET = RouterPaths.smartLeadbotConfig;

/**
 * AGI-352 — where a revoked calendar is reconnected: the Lead Agent config's
 * Integraciones tab, which hosts the Google Calendar connection card in every
 * environment. The Agenda tab hosts the same card but is hidden on production
 * builds, whose deep-link handler already lands `?tab=agenda` on Integraciones
 * — targeting `integrations` directly skips that indirection. Same destination
 * the tray's revoked notification navigated to before this alert replaced it.
 */
export const CALENDAR_RECONNECT_TARGET = `${RouterPaths.smartLeadbotConfig}?tab=integrations`;

/**
 * Ajustes → Suscripción, where the reactivation card for a DROPPED add-on
 * lives. `la-shutdown`'s legacy banner navigated exactly here
 * (`LeadAgentShutdownBanner.component.tsx`); the Alert Center's parity port
 * sent it to the config page instead, which carries no reactivation
 * affordance. `cta_type` is the legacy banner's own analytics param, so
 * Settings keeps attributing the visit the same way.
 */
export const LEAD_AGENT_SHUTDOWN_TARGET = `${RouterPaths.settings}?section=${SettingsTabs.SUBSCRIPTION}&cta_type=lead_agent_shutdown_banner`;

/**
 * RET-2191 — Estadísticas → Agente IA, where the Lead Agent usage bar lives:
 * the one place a customer can see what the cap raise did to their month.
 * Every workspace in the cap-raise cohort has the tab, because its flag is
 * derived from the same active-or-paused entitlement the cohort is cut from.
 */
export const LEAD_AGENT_USAGE_TARGET = `${RouterPaths.analytics}?tab=${eAnalyticsTab.LEAD_AGENT}`;

/**
 * KTD15/U4 — external destinations for the account-health CTA variants,
 * mirroring `AccountHealthBanner`'s INT-1040/INT-1101 config tables (the
 * banner is the copy authority until RET-1879 deletes it; keep the two in
 * sync if Meta's resources move). Variants not listed here use the Intercom
 * kind and need no URL.
 */
export const ACCOUNT_HEALTH_EXTERNAL_TARGETS: Record<string, string> = {
  'status.restricted': 'https://www.facebook.com/business/help',
  'violations.scam': 'https://www.facebook.com/help',
  'violations.tobacco':
    'https://business.whatsapp.com/policy/preview?lang=es_LA',
  'violations.fallback': 'https://business.facebook.com/business/loginpage',
};
